// hls.js 的自訂 loader：把播放清單與片段請求改走 NVR 的回放隧道（F7，@kinio/web-sdk PlaybackTunnel）。
// 播放清單 URL 用假網域 TUNNEL_ORIGIN，讓 hls.js 照常解析相對路徑；loader 把它還原成設備上的 path?query。
// 片段的 byte-range：hls.js 的 rangeEnd 不含（exclusive），隧道的 bytes=<off>-<end> 含（inclusive）。
// 子碼流清單回 404 → 同一個時間窗退主碼流並記在這條隧道（SPEC #device-app，`requestLocal`）。
import type { Loader, LoaderCallbacks, LoaderConfiguration, LoaderContext, LoaderStats } from 'hls.js';
import { localPlaylistPath, type PlaybackTunnel } from '@kinio/web-sdk';

export const TUNNEL_ORIGIN = 'https://nvr-tunnel.invalid';

export function tunnelUrl(path: string): string {
  return TUNNEL_ORIGIN + path;
}

/**
 * 本地回放第一個播放清單的網址：**先要子碼流**（stream 1；SPEC #device-app「本地回放先要子碼流、404 退回主碼流」）。
 * 只錄一路的設備回 404 → `requestLocal` 退主碼流。NVR / Kinio Station 兩路都錄，播子碼流：主碼流 2.6–3.1 Mbps 超過
 * 回放隧道實測約 2.4 Mbps（kinio/plan#91 2026-10-20：NVR631 主碼流 realtime 0.77–0.92、lstw-16ch 子碼流 8.6）。
 */
export function localPlaylistUrl(ch: number, from: number, to: number): string {
  return tunnelUrl(localPlaylistPath(ch, from, to, 1));
}

/** hls.js 的 context → 隧道請求行。 */
export function requestLine(ctx: Pick<LoaderContext, 'url' | 'rangeStart' | 'rangeEnd'>): string {
  const u = new URL(ctx.url);
  const path = u.pathname + u.search;
  if (ctx.rangeStart !== undefined && ctx.rangeEnd !== undefined && ctx.rangeEnd > ctx.rangeStart) {
    return `GET ${path} bytes=${ctx.rangeStart}-${ctx.rangeEnd - 1}`;
  }
  if (ctx.rangeStart !== undefined && ctx.rangeStart > 0) return `GET ${path} bytes=${ctx.rangeStart}-`;
  return `GET ${path}`;
}

/** 子碼流（stream 1）的播放清單 URL → 同一路、同一個時間窗的主碼流（stream 0）；不是子碼流清單 → null。 */
export function mainStreamPlaylist(url: string): string | null {
  const u = new URL(url);
  const m = /^(\/api\/playback\/hls\/\d+\/)1(\.m3u8)$/.exec(u.pathname);
  if (!m) return null;
  u.pathname = `${m[1]}0${m[2]}`;
  return u.toString();
}

type TunnelReply = Awaited<ReturnType<PlaybackTunnel['request']>>;

/** 已知只錄一路（子碼流清單回過 404、主碼流有）的隧道；換隧道（新物件）重新從子碼流試。 */
const mainStreamOnly = new WeakMap<PlaybackTunnel, true>();

const ok = (r: TunnelReply) => r.status === 200 || r.status === 206;

/**
 * 經隧道要一個檔（SPEC #device-app「本地回放先要子碼流、404 退回主碼流」）：子碼流的播放清單回 404 → 同一個時間窗改要主碼流，
 * 主碼流成功就記住這條隧道，之後的清單（含播完接著要的下一個 10 分鐘）直接要 0、不再試 1；主碼流也失敗 → 回子碼流原本的 404、不記。
 * 韌體 bcdee734 起 lsraw 卡只錄一路（主碼流），要另一路回 404。片段與 404 以外的錯誤照原樣。
 */
export async function requestLocal(
  tunnel: PlaybackTunnel,
  ctx: Pick<LoaderContext, 'url' | 'rangeStart' | 'rangeEnd'>,
): Promise<TunnelReply> {
  const main = mainStreamPlaylist(ctx.url);
  if (!main) return tunnel.request(requestLine(ctx));
  const mainLine = () => requestLine({ ...ctx, url: main });
  if (mainStreamOnly.has(tunnel)) return tunnel.request(mainLine());
  const reply = await tunnel.request(requestLine(ctx));
  if (reply.status !== 404) return reply;
  const fallback = await tunnel.request(mainLine());
  if (!ok(fallback)) return reply;
  mainStreamOnly.set(tunnel, true);
  return fallback;
}

function newStats(): LoaderStats {
  const now = performance.now();
  return {
    aborted: false,
    loaded: 0,
    retry: 0,
    total: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: { start: now, first: 0, end: 0 },
    parsing: { start: 0, end: 0 },
    buffering: { start: 0, first: 0, end: 0 },
  };
}

/** 產生綁定某條隧道的 loader class（hls.js 的 config.loader 要的是 class）。 */
export function makeTunnelLoader(getTunnel: () => PlaybackTunnel | null) {
  return class TunnelLoader implements Loader<LoaderContext> {
    context: LoaderContext | null = null;
    stats: LoaderStats = newStats();
    private aborted = false;

    constructor(_config: unknown) {}

    load(context: LoaderContext, _config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>): void {
      this.context = context;
      this.stats = newStats();
      const tunnel = getTunnel();
      if (!tunnel) {
        callbacks.onError({ code: 0, text: 'no playback tunnel' }, context, null, this.stats);
        return;
      }
      // 退到主碼流時照原本的子碼流網址回給 hls.js（同一個目錄，片段相對路徑不變）。
      requestLocal(tunnel, context)
        .then((r) => {
          if (this.aborted) return;
          const now = performance.now();
          this.stats.loading.first = this.stats.loading.first || now;
          this.stats.loading.end = now;
          this.stats.loaded = this.stats.total = r.body.byteLength;
          if (!ok(r)) {
            callbacks.onError({ code: r.status, text: new TextDecoder().decode(r.body).slice(0, 200) }, context, null, this.stats);
            return;
          }
          const data =
            context.responseType === 'arraybuffer'
              ? r.body.buffer.slice(r.body.byteOffset, r.body.byteOffset + r.body.byteLength)
              : new TextDecoder().decode(r.body);
          callbacks.onSuccess({ url: context.url, data }, this.stats, context, null);
        })
        .catch((e: unknown) => {
          if (this.aborted) return;
          const code = (e as { code?: string })?.code;
          if (code === 'TUNNEL_TIMEOUT') callbacks.onTimeout(this.stats, context, null);
          else callbacks.onError({ code: 0, text: String((e as Error)?.message ?? e) }, context, null, this.stats);
        });
    }

    abort(): void {
      this.aborted = true;
      this.stats.aborted = true;
    }

    destroy(): void {
      this.abort();
      this.context = null;
    }
  };
}
